import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Mail, Phone, FolderOpen, Upload, Loader2, X, FileText, Images, LayoutGrid, List, UserCog, Trash2, MessageCircle, AlertTriangle, Search } from "lucide-react";
import ClienteDocsModal from "@/components/ClienteDocsModal";
import { eur as fmtEur, nomeCompleto } from "@/lib/format";
import { esitoUpload } from "@/lib/upload";
import UploadDialog from "@/components/UploadDialog";
import DocumentoDialog from "@/components/GeneraDocumentoDialog";
import AnagraficaDialog from "@/components/AnagraficaDialog";
import AnagraficaFieldsGroups, { requisitiCliente } from "@/components/AnagraficaFieldsGroups";
import ReferenteSelect from "@/components/ReferenteSelect";
import { ImportDocsButtons } from "@/components/ImportDocsButtons";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams, useNavigate, Link } from "react-router-dom";

const pulisci = (s) => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

const EMPTY = { nome: "", cognome: "", email: "", telefono: "", note: "",
  is_azienda: false, pec: "", rappresentante_legale: "",
  codice_fiscale: "", data_nascita: "", luogo_nascita: "", nazionalita: "", indirizzo: "", residenza_citta: "",
  numero_passaporto: "", data_rilascio: "", luogo_rilascio: "", data_scadenza: "",
  numero_documento: "", scadenza_documento: "", rilascio_documento: "", numero_permesso_soggiorno: "", scadenza_permesso: "",
  residenza_tipo: "", residenza_provincia: "", residenza_cap: "", residenza_stato: "",
  luogo_rilascio_permesso: "", data_emissione_permesso: "", luogo_rilascio_documento: "" };

const missingIdDoc = (c) => {
  const val = (k) => String(c[k] || (c.campi_extra || {})[k] || "").trim();
  return !val("numero_passaporto") && !val("numero_documento");
};

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

function IdMissingBadge() {
  return (
    <span data-testid="cliente-id-missing-badge" title="Documento d'identità non registrato in anagrafica (carta d'identità o passaporto)"
      className="inline-flex items-center gap-1 text-[12px] font-bold text-[#9A3B13] whitespace-nowrap">
      <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.8} /> documento d'identità mancante
    </span>
  );
}

export default function Clienti() {
  const zonaFileRef = useRef(null);
  const ultimoDropCliente = useRef(0);
  const qc = useQueryClient();
  const [segn, setSegn] = useState("");
  const { data } = useQuery({ queryKey: ["clienti", segn], queryFn: async () => (await api.get("/clienti", { params: { referente_id: segn || undefined } })).data });
  const { data: referenti } = useQuery({ queryKey: ["referenti"], queryFn: async () => (await api.get("/referenti")).data });
  const { data: attenzione } = useQuery({ queryKey: ["attenzione"], queryFn: async () => (await api.get("/attenzione")).data, refetchInterval: 60000 });
  const { data: pratiche } = useQuery({ queryKey: ["pratiche"], queryFn: async () => (await api.get("/pratiche")).data });
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("grid");
  const [form, setForm] = useState(EMPTY);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [extra, setExtra] = useState({});
  const clienti = data || [];
  const [params] = useSearchParams();
  const [ricerca, setRicerca] = useState(params.get("q") || "");
  const navigate = useNavigate();
  const locFilter = params.get("loc") || "";
  const locMode = params.get("mode") || "origine";
  const normLoc = (s) => (s || "").trim().toUpperCase();
  const shownClientiBase = locFilter
    ? clienti.filter((c) => {
        const t = locMode === "residenza" ? c.residenza_citta : (c.luogo_nascita || c.nazionalita);
        return normLoc(t) === normLoc(locFilter) || normLoc(t).includes(normLoc(locFilter));
      })
    : clienti;
  const cercaClienti = (lista, testo) => {
    const parole = pulisci(testo).split(/\s+/).filter(Boolean);
    if (parole.length === 0) return lista;
    const punteggio = (c) => {
      const nome = pulisci(`${c.nome || ""} ${c.cognome || ""}`);
      const tutto = pulisci([c.nome, c.cognome, c.email, c.telefono, c.codice_fiscale, c.pec,
        c.rappresentante_legale, c.nazionalita, c.luogo_nascita, c.residenza_citta, c.indirizzo,
        c.numero_passaporto, c.numero_documento, ...Object.values(c.campi_extra || {})].join(" "));
      let tot = 0;
      for (const w of parole) {
        if (nome.split(/\s+/).some((x) => x.startsWith(w))) tot += 3;      // inizio di nome/cognome
        else if (nome.includes(w)) tot += 2;                               // dentro nome/cognome
        else if (tutto.includes(w)) tot += 1;                              // altri dati
        else return 0;                                                     // tutte le parole devono esserci
      }
      return tot;
    };
    return lista.map((c) => ({ c, p: punteggio(c) })).filter((x) => x.p > 0)
      .sort((a, b) => b.p - a.p).map((x) => x.c);
  };
  const shownClienti = cercaClienti(shownClientiBase, ricerca);

  const stripExt = (n) => (n || "").replace(/\.[^.]+$/, "");
  const tipiByClient = {};
  (pratiche || []).forEach((p) => { (tipiByClient[p.cliente_id] ||= new Set()).add(p.tipo_pratica_nome); });
  const pendingByClient = {};
  (pratiche || []).forEach((p) => {
    pendingByClient[p.cliente_id] = (pendingByClient[p.cliente_id] || 0) + Math.max(0, (p.importo_totale || 0) - (p.importo_pagato || 0));
  });
  const incassatoByClient = {};
  (pratiche || []).forEach((p) => {
    incassatoByClient[p.cliente_id] = (incassatoByClient[p.cliente_id] || 0) + (p.importo_pagato || 0);
  });

  const LABELS = { nome: "Nome", cognome: "Cognome", luogo_nascita: "Luogo di nascita",
    data_nascita: "Data di nascita", nazionalita: "Nazionalità", residenza_tipo: "Residenza (Italia/Estero)",
    indirizzo: "Via e n. civico", residenza_citta: "Città", residenza_provincia: "Provincia",
    residenza_cap: "CAP", residenza_stato: "Stato di residenza" };

  const create = async (keepOpen = false) => {
    if (form.is_azienda) {
      if (!form.nome) return toast.error("La ragione sociale è obbligatoria");
    } else {
      const mancanti = requisitiCliente((k) => form[k]).filter((k) => !String(form[k] || "").trim());
      if (mancanti.length) return toast.error(`Compila i dati obbligatori: ${mancanti.map((k) => LABELS[k] || k).join(", ")}`);
    }
    setSaving(true);
    try {
      const { data: cliente } = await api.post("/clienti", { ...form, campi_extra: extra });
      if (items.length > 0) {
        const fd = new FormData();
        items.forEach((it) => { fd.append("files", it.file); fd.append("names", it.name || stripExt(it.file.name)); });
        try {
          const { data: up } = await api.post(`/clienti/${cliente.id}/upload`, fd);
          toast.success(esitoUpload(up, "Cliente creato"));
        } catch (e) {
          toast.warning(e?.response?.data?.detail || "Cliente creato, ma upload su Drive non riuscito");
        }
      } else {
        toast.success("Cliente creato");
      }
      setForm(EMPTY); setItems([]); setExtra({}); qc.invalidateQueries({ queryKey: ["clienti"] });
      if (!keepOpen) setOpen(false);
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="clienti-page"
      className="scheda-rb -m-4 sm:-m-5 lg:-m-7 p-4 sm:p-5 lg:p-7 bg-[#F2F1EC] min-h-screen">
      <div className="w-full max-w-[1120px] mx-auto flex flex-col gap-[18px]">

        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex flex-col gap-0.5">
            <h1 className="text-[25px] md:text-[30px] font-bold leading-[1.15] text-[#1B2A4B]">Clienti</h1>
            <p className="text-[14px] md:text-[16px] text-[#4A4A48]" data-testid="clienti-count">
              {shownClienti.length} contatt{shownClienti.length === 1 ? "o" : "i"} in anagrafica
            </p>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <button data-testid="new-cliente-btn" onClick={() => setOpen(true)} className="btn btn-g btn-larghezza sm:w-auto">
              <Plus className="w-4 h-4" /> Nuovo cliente
            </button>
            <div className="hidden md:flex gap-2.5">
              <button data-testid="view-grid" onClick={() => setView("grid")} title="Schede"
                className={`btn btn-piccolo ${view === "grid" ? "btn-p" : "btn-o"}`}><LayoutGrid className="w-4 h-4" /> Schede</button>
              <button data-testid="view-list" onClick={() => setView("list")} title="Elenco"
                className={`btn btn-piccolo ${view === "list" ? "btn-p" : "btn-o"}`}><List className="w-4 h-4" /> Elenco</button>
            </div>
          </div>
        </header>

        <div className="sez px-4 py-3 flex flex-wrap items-center gap-3.5" data-testid="clienti-utility-bar">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-[#5A5A5A] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input data-testid="clienti-search" value={ricerca} onChange={(e) => setRicerca(e.target.value)}
              placeholder="Cerca per nome, telefono, email, codice fiscale, città, passaporto…"
              className="w-full bg-white border border-[#C6C3BA] rounded-[4px] pl-9 pr-9 py-2 text-[14px] focus:outline-none focus:border-[#A8863F] transition-colors" />
            {ricerca && (
              <button data-testid="clienti-search-clear" onClick={() => setRicerca("")} title="Azzera ricerca"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A5A5A] hover:text-[#1B2A4B]">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2.5">
            <label className="text-[11px] font-bold uppercase tracking-[0.6px] text-[#5A5A5A]">Rif.</label>
            <select data-testid="clienti-filter-segnalatore" value={segn} onChange={(e) => setSegn(e.target.value)}
              className="bg-white border border-[#C6C3BA] rounded-[4px] px-3 py-2 text-[14px] focus:outline-none focus:border-[#A8863F]">
              <option value="">Tutti i segnalatori</option>
              <option value="studio">RB Studio</option>
              {(referenti || []).map((r) => <option key={r.id} value={r.id}>{nomeCompleto(r)}</option>)}
            </select>
          </div>
        </div>

        {clienti.length === 0 && (
          <div className="sez p-8 text-center">
            <p className="text-[14px] text-[#5A5A5A]">Nessun cliente. Aggiungine uno o chiedilo a Vera.</p>
          </div>
        )}
        {ricerca && shownClienti.length === 0 && clienti.length > 0 && (
          <p data-testid="clienti-search-count" className="text-[14px] text-[#5A5A5A]">
            Nessun cliente corrisponde a «{ricerca}»
          </p>
        )}

        {locFilter && (
          <div className="sez px-4 py-3 flex items-center justify-between gap-3.5 flex-wrap" data-testid="clienti-geo-filter-banner">
            <span className="text-[14px] text-[#4A4A48]">
              Filtrato per <b>{locMode === "residenza" ? "residenza" : "origine"}</b>: <b>{locFilter}</b>
              {" · "}{shownClienti.length} contatt{shownClienti.length === 1 ? "o" : "i"}
            </span>
            <button data-testid="clienti-geo-filter-clear" onClick={() => navigate("/clienti")} className="btn btn-o btn-piccolo">
              Rimuovi filtro
            </button>
          </div>
        )}

        {/* Schede: sempre su telefono, su computer quando scelte */}
        <div className={`grid sm:grid-cols-2 lg:grid-cols-3 gap-4 ${view === "list" ? "md:hidden" : ""}`}>
          {shownClienti.map((c) => <ClienteCard key={c.id} c={c} qc={qc} daVedere={(attenzione?.clienti || []).includes(c.id)} />)}
        </div>

        {/* Elenco: solo computer */}
        {view === "list" && (
          <div className="hidden md:block sez overflow-hidden" data-testid="clienti-elenco">
            <div className="subt">
              <span>Elenco</span>
              <span className="text-[12px] font-normal normal-case tracking-normal text-[#5A5A5A]">
                {shownClienti.length} contatti
              </span>
            </div>
            {shownClienti.map((c) => (
              <ClienteRow key={c.id} c={c} qc={qc} tipi={[...(tipiByClient[c.id] || [])]}
                pending={pendingByClient[c.id] || 0} incassato={incassatoByClient[c.id] || 0}
                fmtEur={fmtEur} daVedere={(attenzione?.clienti || []).includes(c.id)} />
            ))}
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-4 border-t-2 border-[#C9A84C] shadow-lg max-h-[90vh] overflow-y-auto">
            <h3 className="font-serif text-2xl text-[#1A2B4A] mb-4">Nuovo cliente</h3>
            <div className="mb-4">
              <ImportDocsButtons currentValues={{ ...form, ...extra }} onExtract={(f, enriched) => {
                setForm((prev) => ({ ...prev, ...f }));
                const cognome = (f.cognome || form.cognome || "").trim();
                const nome = (f.nome || form.nome || "").trim();
                const cli = [cognome, nome].filter(Boolean).join(" ");
                // il riquadro in alto serve a compilare l'anagrafica: allega solo se ha riconosciuto dati
                if (Object.keys(f || {}).length > 0 && enriched?.length) setItems((prev) => [...prev, ...enriched.map(({ file, tipo }) => {
                  const base = (tipo || stripExt(file.name) || "Documento").trim();
                  return { uid: uid(), file, name: cli ? `${base} (${cli})` : base };
                })]);
              }} />
              <p className="text-[11px] text-[#4a5568] mt-1.5">
                Questo riquadro serve <b>solo a compilare l'anagrafica</b> leggendo un documento d'identità (passaporto, carta d'identità, permesso).
                Se l'AI non riconosce dati utili, il file <b>non viene caricato</b>: per conservarlo usa la sezione “Documenti” in fondo.
              </p>
            </div>
            <label className="flex items-center gap-2 mb-4 text-sm text-[#1A2B4A] cursor-pointer select-none" data-testid="cliente-is-azienda-label">
              <input type="checkbox" data-testid="cliente-is-azienda" checked={!!form.is_azienda}
                onChange={(e) => setForm({ ...form, is_azienda: e.target.checked })} className="accent-[#C9A84C] w-4 h-4" />
              È un'azienda / persona giuridica
            </label>

            {form.is_azienda ? (
              <div className="rounded-lg border border-[#1A2B4A]/15 bg-white p-4 mb-4" data-testid="cliente-azienda-fields">
                <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#1A2B4A] mb-3">Dati azienda</h4>
                <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3">
                  {[["nome", "Ragione sociale"], ["codice_fiscale", "Partita IVA / Codice Fiscale"], ["indirizzo", "Sede legale (indirizzo)"], ["pec", "PEC"], ["email", "Email"], ["rappresentante_legale", "Rappresentante legale"], ["telefono", "Telefono"]].map(([k, label]) => (
                    <div key={k} className={k === "indirizzo" ? "sm:col-span-2" : ""}>
                      <label className="text-xs text-[#4a5568]">{label}</label>
                      <input data-testid={`cliente-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                        className="w-full mt-1 px-2.5 py-1.5 text-sm bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <AnagraficaFieldsGroups
                    tipiPratica={[]}
                    onlyBuiltin
                    baseRequired
                    getValue={(f) => (f.builtin ? form[f.key] : extra[f.key])}
                    setValue={(f, v) => (f.builtin ? setForm((p) => ({ ...p, [f.key]: v })) : setExtra((p) => ({ ...p, [f.key]: v })))}
                  />
                </div>
              </>
            )}

            <div className="mb-4">
              <ReferenteSelect value={form.referente_id} onChange={(id) => setForm((p) => ({ ...p, referente_id: id }))} testid="cliente-referente" />
            </div>

            <label className="block text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Documenti da conservare (opzionale)</label>
            <p className="text-xs text-[#4a5568] mt-1 mb-2">
              Qualsiasi documento, anche non anagrafico: <b>non compila alcun campo</b>. Puoi <b>rinominare</b> ogni file qui sotto
              per ritrovarlo facilmente nella cartella Drive del cliente.
            </p>
            <div data-testid="cliente-upload-zone" onDragEnter={(e) => e.preventDefault()} onDragOver={(e) => e.preventDefault()}
              onClick={(e) => { if (e.currentTarget.contains(e.target) && Date.now() - ultimoDropCliente.current > 1500) zonaFileRef.current?.click(); }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); ultimoDropCliente.current = Date.now(); const dropped = Array.from(e.dataTransfer.files || []); if (dropped.length) setItems([...items, ...dropped.map((f) => ({ uid: uid(), file: f, name: stripExt(f.name) }))]); }}
              className="flex flex-col items-center justify-center gap-1 border border-dashed border-[#1A2B4A]/30 rounded-sm py-6 cursor-pointer hover:border-[#C9A84C] transition-colors">
              <Upload className="w-5 h-5 text-[#1A2B4A]" />
              <span className="text-xs text-[#4a5568]">Trascina qui i file o clicca per selezionare</span>
              <input ref={zonaFileRef} data-testid="cliente-file-input" type="file" multiple accept="image/*,application/pdf,.doc,.docx"
                className="hidden" onChange={(e) => setItems([...items, ...Array.from(e.target.files).map((f) => ({ uid: uid(), file: f, name: stripExt(f.name) }))])} />
            </div>
            {items.length > 0 && (
              <div className="mt-3 space-y-2">
                {items.map((it, i) => (
                  <div key={it.uid} className="border border-[#1A2B4A]/10 rounded-sm p-2.5">
                    <div className="flex items-center gap-2 text-[11px] text-[#4a5568] mb-1.5">
                      <FileText className="w-3.5 h-3.5" /> <span className="truncate">{it.file.name}</span>
                      <button className="ml-auto" onClick={() => setItems(items.filter((_, j) => j !== i))}><X className="w-3.5 h-3.5 text-[#9b2c2c]" /></button>
                    </div>
                    <div className="flex gap-2">
                      <input data-testid={`cliente-doc-name-${i}`} value={it.name}
                        onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                        placeholder="es. Passaporto"
                        className="flex-1 px-3 py-1.5 text-sm bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
                      <button title="Aggiungi nome cliente"
                        onClick={() => setItems(items.map((x, j) => j === i ? { ...x, name: (x.name || "") + ` (${[form.cognome, form.nome].filter(Boolean).join(" ")})` } : x))}
                        className="inline-flex items-center gap-1 text-xs border border-[#1A2B4A]/20 rounded-sm px-2 hover:border-[#C9A84C]">
                        <Plus className="w-3 h-3" /> cliente
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-3 mt-6">
              <button onClick={() => { setOpen(false); setItems([]); setForm(EMPTY); setExtra({}); }} className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-sm hover:bg-[#f9f8f5]">Annulla</button>
              <button data-testid="cliente-create-add-another" onClick={() => create(true)} disabled={saving}
                className="px-4 py-2 text-sm border border-[#C9A84C] text-[#1A2B4A] rounded-sm hover:bg-[#f6f1e2] transition-colors flex items-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Salva e aggiungi un altro
              </button>
              <button data-testid="cliente-create" onClick={() => create(false)} disabled={saving}
                className="px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-sm hover:bg-[#22345c] transition-colors flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DeleteClienteBtn({ c, qc, variant = "card", iconOnly = false }) {
  const { can } = useAuth();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!can("elimina_clienti")) return null;
  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/clienti/${c.id}`);
      toast.success(`Cliente ${c.nome} ${c.cognome} eliminato`);
      setConfirm(false);
      qc.invalidateQueries({ queryKey: ["clienti"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eliminazione non riuscita");
    } finally { setBusy(false); }
  };
  return (
    <>
      {iconOnly ? (
        <button data-testid={`cliente-delete-btn-${c.id}`} onClick={() => setConfirm(true)} title="Elimina"
          className="text-[#8A2E2E]/60 hover:text-[#8A2E2E] transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      ) : (
        <button data-testid={`cliente-delete-btn-${c.id}`} onClick={() => setConfirm(true)}
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#8A2E2E] hover:text-[#6b1f1f] transition-colors">
          <Trash2 className="w-3.5 h-3.5" /> Elimina
        </button>
      )}
      {confirm && createPortal(
        <div className="fixed inset-0 z-[60] bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cliente-delete-dialog">
          <div className="bg-white rounded-lg max-w-sm w-full p-4 border-t-2 border-[#9b2c2c]">
            <h3 className="font-serif text-xl text-[#1A2B4A] mb-2">Elimina cliente</h3>
            <p className="text-sm text-[#4a5568] mb-1">Vuoi eliminare <b>{c.nome} {c.cognome}</b>?</p>
            <p className="text-xs text-[#4a5568] mb-5">La cartella Drive del cliente NON verrà eliminata. Se il cliente ha pratiche collegate, l'eliminazione sarà bloccata.</p>
            <div className="flex justify-end gap-3">
              <button data-testid="cliente-delete-cancel" onClick={() => setConfirm(false)} className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-sm hover:bg-[#f9f8f5]">Annulla</button>
              <button data-testid="cliente-delete-confirm" onClick={doDelete} disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#9b2c2c] text-white rounded-sm hover:bg-[#7a1f1f] disabled:opacity-50">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Elimina
              </button>
            </div>
          </div>
        </div>, document.body)}
    </>
  );
}

function ClienteCard({ c, qc, daVedere }) {
  const [docsOpen, setDocsOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [anaOpen, setAnaOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);

  const azione = "inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#8A6E2F] hover:text-[#6B5523] transition-colors";

  return (
    <div data-testid="cliente-card" title={daVedere ? "Ha pratiche con dati o documenti da completare" : undefined}
      className={`sez p-4 transition-colors hover:border-[#A8863F] ${daVedere ? "border-l-[3px] border-l-[#9A3B13]" : ""}`}
      style={daVedere ? { background: "#FDF6F1" } : undefined}>
      <Link data-testid="cliente-name-link" to={`/clienti/${c.id}`}
        className="block text-[16px] font-bold text-[#1B2A4B] no-underline hover:text-[#A8863F] transition-colors break-words">
        {c.nome} {c.cognome}
      </Link>
      {!c.is_azienda && missingIdDoc(c) && <div className="mt-1"><IdMissingBadge /></div>}
      <div className="mt-2.5 flex flex-col gap-1 text-[14px] text-[#4A4A48]">
        {c.email && <span className="flex items-center gap-2 min-w-0"><Mail className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{c.email}</span></span>}
        {c.telefono && <span className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 shrink-0" /> {c.telefono}</span>}
        <span data-testid="cliente-card-segnalatore" className="text-[12px] text-[#5A5A5A]">
          Rif. {c.referente ? [c.referente.cognome, c.referente.nome].filter(Boolean).join(" ") : "RB Studio"}
        </span>
      </div>
      <div className="mt-3 pt-2.5 border-t border-[#F0EEE8] flex flex-wrap items-center gap-x-3.5 gap-y-2">
        <button data-testid="cliente-anagrafica-btn" onClick={() => setAnaOpen(true)} className={azione}>
          <UserCog className="w-3.5 h-3.5" /> Anagrafica
        </button>
        <button data-testid="cliente-docs-btn" onClick={() => setDocsOpen(true)} className={azione}>
          <Images className="w-3.5 h-3.5" /> Documenti
        </button>
        <button data-testid="cliente-genera-doc-btn" onClick={() => setGenOpen(true)} className={azione}>
          <FileText className="w-3.5 h-3.5" /> Documento
        </button>
        {c.drive_folder && (
          <a href={c.drive_folder} target="_blank" rel="noreferrer" data-testid="cliente-drive-link" className={`${azione} no-underline`}>
            <FolderOpen className="w-3.5 h-3.5" /> Drive
          </a>
        )}
        <button data-testid="cliente-upload-btn" onClick={() => setUploadOpen(true)} className={azione}>
          <Upload className="w-3.5 h-3.5" /> Carica
        </button>
        <DeleteClienteBtn c={c} qc={qc} variant="card" />
      </div>
      {anaOpen && <AnagraficaDialog cliente={c} onClose={() => setAnaOpen(false)} onSaved={() => qc.invalidateQueries({ queryKey: ["clienti"] })} />}
      {docsOpen && <ClienteDocsModal cliente={c} onClose={() => setDocsOpen(false)} />}
      {uploadOpen && <UploadDialog cliente={c} onClose={() => setUploadOpen(false)} onUploaded={() => qc.invalidateQueries({ queryKey: ["clienti"] })} />}
      {genOpen && createPortal(<DocumentoDialog open={genOpen} onClose={() => setGenOpen(false)} clienteId={c.id} clienteNome={`${c.nome} ${c.cognome}`}
        onGenerated={() => qc.invalidateQueries({ queryKey: ["clienti"] })} />, document.body)}
    </div>
  );
}


function ClienteRow({ c, qc, tipi, pending, incassato, fmtEur, daVedere }) {
  const [docsOpen, setDocsOpen] = useState(false);
  const [anaOpen, setAnaOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const rawPhone = (c.telefono || "").trim() || (c.referente?.telefono || "").trim();
  const waSource = c.telefono?.trim() ? "cliente" : (c.referente?.telefono?.trim() ? "Rif." : null);
  let waDigits = rawPhone.replace(/[^\d]/g, "");
  if (waDigits.length === 10 && !rawPhone.startsWith("+") && !waDigits.startsWith("39")) waDigits = "39" + waDigits;
  const waLink = waDigits ? `https://wa.me/${waDigits}` : null;
  const tipiStr = tipi.length ? tipi.join(" · ") : "—";
  const refLabel = c.referente ? [c.referente.cognome, c.referente.nome].filter(Boolean).join(" ") : "RB Studio";
  const iconBtn = "text-[#5A5A5A] hover:text-[#A8863F] transition-colors";
  return (
    <div data-testid="cliente-row" title={daVedere ? "Ha pratiche con dati o documenti da completare" : undefined}
      className={`riga group gap-4 hover:bg-[#FAFAF7] transition-colors ${daVedere ? "border-l-[3px] border-l-[#9A3B13]" : ""}`}
      style={daVedere ? { background: "#FDF6F1" } : undefined}>
      <span className="flex-[999_1_240px] min-w-0 flex flex-col gap-0.5">
        <Link data-testid="cliente-name-link" to={`/clienti/${c.id}`}
          className="text-[15px] font-bold text-[#1B2A4B] no-underline hover:text-[#A8863F] transition-colors break-words">
          {c.nome} {c.cognome}
        </Link>
        <span className="text-[13px] text-[#4A4A48] truncate">{tipiStr}</span>
        <span data-testid="cliente-row-rif" className="text-[12px] text-[#5A5A5A] truncate">
          Rif. {refLabel}
          {!c.is_azienda && missingIdDoc(c) ? " · " : ""}
          {!c.is_azienda && missingIdDoc(c) && <span className="font-bold text-[#9A3B13]">documento d'identità mancante</span>}
        </span>
      </span>
      <span className="w-[110px] shrink-0 text-right" data-testid="cliente-incassato">
        <span className={`text-[15px] font-bold ${incassato > 0 ? "text-[#2E5E3A]" : "text-[#5A5A5A]"}`}>
          {incassato > 0 ? fmtEur(incassato) : "—"}
        </span>
        <span className="block text-[12px] text-[#5A5A5A]">incassato</span>
      </span>
      <span className="w-[110px] shrink-0 text-right" data-testid="cliente-pending">
        <span className={`text-[15px] font-bold ${pending > 0 ? "text-[#1A1A1A]" : "text-[#2E5E3A]"}`}>
          {pending > 0 ? fmtEur(pending) : "—"}
        </span>
        <span className="block text-[12px] text-[#5A5A5A]">da incassare</span>
      </span>
      <span className="shrink-0 flex items-center gap-2.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button data-testid="row-ana-btn" onClick={() => setAnaOpen(true)} title="Anagrafica" className={iconBtn}><UserCog className="w-4 h-4" /></button>
        {waLink
          ? <a data-testid="row-whatsapp-btn" href={waLink} target="_blank" rel="noreferrer" title={`WhatsApp (numero ${waSource})`}
              className="text-[#2E5E3A]/70 hover:text-[#2E5E3A]"><MessageCircle className="w-4 h-4" /></a>
          : <button data-testid="row-whatsapp-btn" disabled title="Nessun numero disponibile (né cliente né Rif.)"
              className="text-[#2E5E3A]/25 cursor-not-allowed"><MessageCircle className="w-4 h-4" /></button>}
        <button data-testid="row-docs-btn" onClick={() => setDocsOpen(true)} title="Documenti" className={iconBtn}><Images className="w-4 h-4" /></button>
        <button data-testid="row-upload-btn" onClick={() => setUploadOpen(true)} title="Carica documenti" className={iconBtn}><Upload className="w-4 h-4" /></button>
        <button data-testid="row-genera-doc-btn" onClick={() => setGenOpen(true)} title="Genera documento" className={iconBtn}><FileText className="w-4 h-4" /></button>
        {c.drive_folder && (
          <a data-testid="row-drive-link" href={c.drive_folder} target="_blank" rel="noreferrer" title="Cartella Drive" className={iconBtn}><FolderOpen className="w-4 h-4" /></a>
        )}
        <DeleteClienteBtn c={c} qc={qc} variant="row" iconOnly />
      </span>
      {anaOpen && createPortal(<AnagraficaDialog cliente={c} onClose={() => setAnaOpen(false)} onSaved={() => qc.invalidateQueries({ queryKey: ["clienti"] })} />, document.body)}
      {docsOpen && createPortal(<ClienteDocsModal cliente={c} onClose={() => setDocsOpen(false)} />, document.body)}
      {genOpen && createPortal(<DocumentoDialog open={genOpen} onClose={() => setGenOpen(false)} clienteId={c.id} clienteNome={`${c.nome} ${c.cognome}`} />, document.body)}
      {uploadOpen && createPortal(<UploadDialog cliente={c} onClose={() => setUploadOpen(false)}
        onUploaded={() => qc.invalidateQueries({ queryKey: ["clienti"] })} />, document.body)}
    </div>
  );
}
